/**
 * Tool confirmation: kick off an on-demand prospecting batch.
 *
 * Input shape: {
 *   campaignId?: string,
 *   contactListId?: number,
 *   requestedCount: number,
 *   filters?: PipelineFilters,
 *   estimatedCostPence?: number
 * }
 *
 * Lets the user adjust the count + confirm. Cost estimate displayed when
 * the server provided one.
 */

import { useState } from 'react';
import { UsersIcon } from '@heroicons/react/24/outline';
import { Button } from '@/components/ui/button';
import type { ToolConfirmationProps } from '../tool-registry';

export function RunProspectingBatchTool({ input, onConfirm, onReject }: ToolConfirmationProps) {
  const [count, setCount] = useState((input.requestedCount as number) ?? 50);
  const estimatedCostPence = input.estimatedCostPence as number | undefined;
  const campaignName = input.campaignName as string | undefined;
  const filters = input.filters as Record<string, unknown> | undefined;

  return (
    <div className="h-full flex items-center justify-center p-8">
      <div className="bg-card border border-border rounded-2xl p-6 max-w-lg w-full shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-success-surface flex items-center justify-center">
            <UsersIcon className="h-5 w-5 text-success-foreground" />
          </div>
          <div>
            <h2 className="text-lg font-semibold">Run a prospecting batch</h2>
            {campaignName && (
              <p className="text-xs text-muted-foreground">For {campaignName}</p>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">
              How many prospects?
            </label>
            <input
              type="number"
              min={1}
              max={10_000}
              value={count}
              onChange={(e) => setCount(parseInt(e.target.value || '0', 10))}
              className="w-full text-sm px-3 py-2 rounded-lg border border-control-border bg-input-bg"
            />
          </div>

          {filters && (
            <div>
              <label className="text-xs font-medium text-muted-foreground block mb-1">
                Filters
              </label>
              <pre className="text-xs bg-surface-sunken border border-border rounded-lg p-3 max-h-40 overflow-y-auto">
                {JSON.stringify(filters, null, 2)}
              </pre>
            </div>
          )}

          {estimatedCostPence != null && (
            <p className="text-xs text-muted-foreground">
              Estimated cost: <span className="font-medium text-foreground">£{(estimatedCostPence / 100).toFixed(2)}</span>
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5">
          <Button variant="secondary" onClick={onReject}>Cancel</Button>
          <Button variant="primary" onClick={() => onConfirm({ ...input, requestedCount: count })}>
            Queue batch
          </Button>
        </div>
      </div>
    </div>
  );
}
